import { useCallback, useEffect, useState } from "react";
import { SmartTable } from "@/components/SmartTable";
import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";
import { AlertTriangle, Database, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FilterBar, PageHeader } from "@/components/FilterBar";
import { RapportMoisDernier } from "@/components/RapportMoisDernier";
import { KpiCard, Panel } from "@/components/KpiCard";
import { REGUL_LABELS } from "@/components/Badges";
import { DeclarationBanner } from "@/components/DeclarationBanner";
import { BauxBanner } from "@/components/BauxBanner";
import { AlerteEcartTaxes } from "@/components/AlerteEcartTaxes";
import { RappelAcomptes } from "@/components/RappelAcomptes";
import { RappelDemandes } from "@/components/RappelDemandes";
import { ExportTableauBord } from "@/components/ExportTableauBord";
import { NonRapprochesKpi } from "@/components/NonRapprochesKpi";
import { AlerteBanque } from "@/components/AlerteBanque";
import { SeuilPanel } from "@/components/SeuilPanel";
import { ReclamationsPanel } from "@/components/ReclamationsPanel";
import { ParLogementPanel } from "@/components/ParLogementPanel";
import { RepartitionPanel } from "@/components/RepartitionPanel";
import { SoldeMensuelPanel } from "@/components/SoldeMensuelPanel";
import { SoldeImmeublesPanel } from "@/components/SoldeImmeublesPanel";
import { useFilters } from "@/context/FilterContext";
import { api, cleanParams, errMsg, money, moisLabel } from "@/lib/api";


const EmptyState = ({ onDemo, loading }) => (
  <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center rise" data-testid="dashboard-empty">
    <Database className="mx-auto h-8 w-8 text-slate-400" />
    <h2 className="mt-4 text-lg font-semibold">Aucune donnée pour l'instant</h2>
    <p className="mt-1 text-sm text-slate-500">Importez votre relevé bancaire et vos rapports Airbnb / Booking, ou essayez avec l'exemple.</p>
    <div className="mt-6 flex flex-col sm:flex-row justify-center gap-2">
      <Button asChild className="bg-emerald-700 hover:bg-emerald-800" data-testid="empty-go-imports">
        <Link to="/imports">Importer mes fichiers</Link>
      </Button>
      <Button variant="outline" onClick={onDemo} disabled={loading} data-testid="load-demo-button">
        {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Charger l'exemple
      </Button>
    </div>
  </div>
);

const MonthlyChart = ({ data }) => (
  <ResponsiveContainer width="100%" height={260}>
    <BarChart data={data.map((d) => ({ ...d, label: moisLabel(d.mois) }))}>
      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
      <YAxis tick={{ fontSize: 11 }} />
      <Tooltip formatter={(v) => money(v)} />
      <Legend wrapperStyle={{ fontSize: 12 }} />
      <Bar dataKey="Airbnb" stackId="a" fill="#FF385C" />
      <Bar dataKey="Booking" stackId="a" fill="#003580" radius={[3, 3, 0, 0]} />
      <Bar dataKey="ecart" name="Écart fiscal" fill="#DC2626" radius={[3, 3, 0, 0]} />
    </BarChart>
  </ResponsiveContainer>
);

const TaxTable = ({ rows }) => (
  <div className="overflow-x-auto">
    <SmartTable id="taxes-plateforme" className="w-full text-sm" data-testid="tax-by-platform-table">
      <thead>
        <tr className="text-left text-[11px] font-mono uppercase tracking-wider text-slate-500">
          <th className="py-2">Plateforme</th><th className="text-right">Rés.</th><th className="text-right">Versées</th>
          <th className="text-right">Attendues</th><th className="text-right">Écart</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p) => (
          <tr key={p.plateforme} className="border-t border-slate-100">
            <td className="py-2.5 font-medium">{p.plateforme}</td>
            <td className="text-right tabular">{p.nb}</td>
            <td className="text-right tabular">{money(p.taxes_versees)}</td>
            <td className="text-right tabular">{money(p.taxes_attendues)}</td>
            <td className={`text-right tabular font-semibold ${p.ecart > 0.05 ? "text-red-600" : "text-emerald-700"}`}>{money(p.ecart)}</td>
          </tr>
        ))}
      </tbody>
    </SmartTable>
  </div>
);

const Anomalies = ({ items }) =>
  items.length === 0 ? (
    <p className="text-sm text-slate-500" data-testid="no-anomalies">Aucune dépense inhabituelle détectée (3 opérations min. par catégorie).</p>
  ) : (
    <ul className="space-y-2" data-testid="anomalies-list">
      {items.map((a) => (
        <li key={a.id} className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50/50 p-3 text-sm">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div><p className="font-medium">{a.description} <span className="text-xs text-slate-500">· {a.categorie} · {a.date}</span></p>
            <p className="text-xs text-slate-600 tabular">{a.alerte}</p></div>
        </li>
      ))}
    </ul>
  );

export default function Dashboard() {
  const { filters, refreshOptions } = useFilters();
  const [data, setData] = useState(null);
  const [demoLoading, setDemoLoading] = useState(false);

  const load = useCallback(() => {
    api.get("/dashboard", { params: cleanParams(filters) }).then((r) => {
      setData(r.data);
      (r.data.reclamations_cloturees || []).forEach((c) => toast.success(`Réclamation ${c.code} régularisée : dépôt ${c.plateforme} de ${money(c.montant)} reçu le ${c.date}`));
    }).catch((e) => toast.error(errMsg(e)));
  }, [filters]);

  useEffect(load, [load]);

  const loadDemo = async () => {
    setDemoLoading(true);
    try {
      await api.post("/demo");
      await api.post("/rapprochement/auto");
      await refreshOptions();
      toast.success("Exemple chargé et rapprochement proposé");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setDemoLoading(false);
    }
  };

  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-emerald-700" data-testid="dashboard-loading" />;
  const k = data.kpis;
  const empty = k.nb_reservations === 0 && k.total_depenses === 0 && filters.mois === "all";

  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow="Vue d'ensemble" title="Tableau de bord"><FilterBar /></PageHeader>
      <AlerteEcartTaxes />
      <RappelAcomptes />
      <RappelDemandes />
      <AlerteBanque />
      {!empty && <div className="-mt-4 mb-6 flex flex-wrap justify-end gap-2"><ExportTableauBord filters={filters} /><RapportMoisDernier /></div>}
      {!empty && <DeclarationBanner refreshKey={data} />}
      {!empty && <BauxBanner refreshKey={data} />}
      {empty ? <EmptyState onDemo={loadDemo} loading={demoLoading} /> : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <KpiCard label="Revenus bruts" value={money(k.revenus_bruts)} sub={`${k.nb_reservations} réservations · loyer + ménage`} testId="kpi-revenus" className="rise-1" />
            <KpiCard label="Commissions" value={money(k.commissions)} sub={`Versé net : ${money(k.total_verse)}`} testId="kpi-commissions" className="rise-2" />
            <KpiCard label="Écart fiscal TPS/TVQ" value={money(k.ecart_fiscal)} sub={`${k.nb_erreurs} réservation(s) en erreur`} tone={k.ecart_fiscal > 0 ? "danger" : "good"} testId="kpi-ecart" className="rise-3" />
            <NonRapprochesKpi k={k} detail={data.non_rapproches} />
          </div>
          <SeuilPanel refreshKey={data} />
          <ReclamationsPanel refreshKey={data} />
          <ParLogementPanel rows={data.par_logement} onChange={load} />
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <Panel title="Revenus mensuels par plateforme" className="xl:col-span-2" testId="monthly-chart">
              {data.mensuel.length ? <MonthlyChart data={data.mensuel} /> : <p className="text-sm text-slate-500">Aucune réservation.</p>}
            </Panel>
            <Panel title="Suivi des régularisations" testId="regul-summary" action={<Link to="/reservations" className="text-xs text-emerald-700 hover:underline" data-testid="regul-see-all">Voir</Link>}>
              <ul className="space-y-3">
                {Object.entries(data.regularisations).map(([s, n]) => (
                  <li key={s} className="flex justify-between text-sm"><span className="text-slate-600">{REGUL_LABELS[s]}</span><span className="tabular font-semibold" data-testid={`regul-count-${s}`}>{n}</span></li>
                ))}
                <li className="flex justify-between text-sm border-t border-slate-100 pt-3"><span className="text-amber-700">Relances dues</span><span className="tabular font-semibold text-amber-700" data-testid="regul-relances-dues">{data.relances_dues}</span></li>
              </ul>
            </Panel>
          </div>
          <RepartitionPanel depenses={data.depenses_categories} depensesAbregees={data.depenses_abregees || []} detailAbregees={data.depenses_abregees_detail || {}} revenus={data.revenus_categories || []} detailDepenses={data.depenses_detail || {}} detailRevenus={data.revenus_detail || {}} mois={filters.mois}
            classement={{ choix: data.categories_choix || [], choixRevenus: data.revenus_choix || [], immeubles: data.immeubles_choix || [], rubriques: data.rubriques_par_immeuble || [], onClasse: load }} />
          <SoldeMensuelPanel rows={data.solde_mensuel || []} />
          <SoldeImmeublesPanel mois={filters.mois} />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <Panel title="Taxes par plateforme"><TaxTable rows={data.par_plateforme} /></Panel>
            <Panel title="Anomalies de dépenses"><Anomalies items={data.anomalies} /></Panel>
          </div>
        </div>
      )}
    </div>
  );
}
